/**
 * clusterMatch — suggests which existing cluster a pasted keyword belongs to.
 *
 * Used by the Content Plan's "Paste keywords" modal so a bulk paste arrives
 * pre-sorted for review rather than dumped into one cluster. Scoring is a
 * TF-IDF-ish overlap against each cluster's existing titles and target
 * keywords: tokens that appear in every cluster ("seo", "google") carry almost
 * no weight, so the signal comes from the distinctive terms ("backlink",
 * "citation", "schema").
 *
 * Deliberately local and instant — the operator reviews and overrides every
 * row, so a fast approximate map beats a slow model call.
 */

/** Terms that carry no cluster signal: listicle scaffolding, years, filler. */
const STOPWORDS = new Set([
  "a", "an", "and", "are", "as", "at", "be", "best", "but", "by", "can", "compared", "complete",
  "do", "does", "for", "free", "from", "guide", "how", "in", "into", "is", "it", "its", "list",
  "of", "on", "or", "our", "should", "so", "term", "that", "the", "to", "top", "vs", "want",
  "what", "when", "which", "who", "why", "will", "with", "you", "your", "2024", "2025", "2026", "2027",
]);

/** Light suffix stemmer so tracker/tracking/track and tool/tools collapse together. */
function stem(word: string): string {
  let w = word;
  for (const suffix of ["ing", "ers", "er", "ies", "es", "s"]) {
    if (w.length > suffix.length + 2 && w.endsWith(suffix)) {
      w = w.slice(0, -suffix.length);
      break;
    }
  }
  // "ies" → "i" reads better as "y" (strategies → strategy)
  if (w.endsWith("i")) w = `${w.slice(0, -1)}y`;
  return w;
}

export function tokenize(text: string): string[] {
  return text
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter(t => t.length > 1 && !STOPWORDS.has(t))
    .map(stem);
}

export interface ClusterIndex {
  clusters: string[];
  /** cluster → token → weight within that cluster (already normalized). */
  profiles: Map<string, Map<string, number>>;
  /** token → inverse document frequency across clusters. */
  idf: Map<string, number>;
}

export interface ClusterSuggestion {
  cluster: string | null;
  /** 0–1. Share of the total score won by the top cluster; <0.4 means "review this". */
  confidence: number;
}

export interface IndexableItem {
  cluster: string;
  title: string;
  targetKeyword?: string;
}

/**
 * Build the scoring index from the current plan. The cluster's own name is
 * weighted heavily — "Link Building" should own "link building" outright even
 * before any article in it does.
 */
export function buildClusterIndex(items: IndexableItem[]): ClusterIndex {
  const NAME_WEIGHT = 6;
  const raw = new Map<string, Map<string, number>>();

  const bump = (cluster: string, token: string, by: number) => {
    const m = raw.get(cluster) ?? new Map<string, number>();
    m.set(token, (m.get(token) ?? 0) + by);
    raw.set(cluster, m);
  };

  const clusters = Array.from(new Set(items.map(i => i.cluster))).filter(Boolean);
  for (const cluster of clusters) {
    for (const t of tokenize(cluster)) bump(cluster, t, NAME_WEIGHT);
  }
  for (const item of items) {
    if (!item.cluster) continue;
    for (const t of tokenize(item.title)) bump(item.cluster, t, 1);
    // The target keyword is the sharpest signal a cluster has about its scope.
    if (item.targetKeyword) for (const t of tokenize(item.targetKeyword)) bump(item.cluster, t, 2);
  }

  // Normalize so large clusters don't swamp small ones. Deliberately sqrt and
  // not the full total: dividing by size outright over-rewards tiny clusters,
  // which then win on a single incidental mention.
  const profiles = new Map<string, Map<string, number>>();
  for (const [cluster, counts] of raw) {
    let total = 0;
    for (const v of counts.values()) total += v;
    const denom = Math.sqrt(total || 1);
    const norm = new Map<string, number>();
    for (const [token, v] of counts) norm.set(token, v / denom);
    profiles.set(cluster, norm);
  }

  // IDF over clusters-as-documents.
  const df = new Map<string, number>();
  for (const counts of profiles.values()) {
    for (const token of counts.keys()) df.set(token, (df.get(token) ?? 0) + 1);
  }
  const n = profiles.size || 1;
  const idf = new Map<string, number>();
  for (const [token, d] of df) idf.set(token, Math.log((n + 1) / (d + 0.5)));

  return { clusters, profiles, idf };
}

/**
 * Score a keyword against every cluster. Returns the best match plus how much
 * to trust it, so the UI can flag the rows worth a second look.
 *
 * Confidence deliberately combines two independent things:
 *   • margin   — how far the winner beat the runner-up.
 *   • coverage — how much of the keyword's meaning the winner actually explains.
 *
 * Margin alone is misleading: a keyword like "best profound alternatives" whose
 * only matched token is the incidental "alternatives" wins its cluster
 * outright, scoring a perfect margin on almost no evidence. Coverage catches
 * that, because the token carrying the real meaning ("profound") is unknown to
 * every cluster — a strong hint this keyword needs a cluster of its own.
 */
export function suggestCluster(keyword: string, index: ClusterIndex): ClusterSuggestion {
  const tokens = tokenize(keyword);
  if (tokens.length === 0 || index.profiles.size === 0) return { cluster: null, confidence: 0 };

  const scores: Array<{ cluster: string; score: number }> = [];
  for (const [cluster, profile] of index.profiles) {
    let score = 0;
    for (const t of tokens) {
      const tf = profile.get(t);
      if (!tf) continue;
      score += tf * (index.idf.get(t) ?? 1);
    }
    scores.push({ cluster, score });
  }

  scores.sort((a, b) => b.score - a.score);
  const top = scores[0];
  if (!top || top.score <= 0) return { cluster: null, confidence: 0 };
  const second = scores[1]?.score ?? 0;
  const margin = top.score / (top.score + second);

  // Coverage: share of the keyword's total token weight that the winning
  // cluster explains. A token no cluster has ever seen counts at the maximum
  // idf, so unknown terms drag confidence down hard.
  const n = index.profiles.size;
  const maxIdf = Math.log((n + 1) / 0.5);
  const winner = index.profiles.get(top.cluster);
  let matchedWeight = 0;
  let totalWeight = 0;
  for (const t of tokens) {
    const idf = index.idf.get(t);
    if (idf === undefined) { totalWeight += maxIdf; continue; }
    totalWeight += idf;
    if (winner?.has(t)) matchedWeight += idf;
  }
  const coverage = totalWeight > 0 ? matchedWeight / totalWeight : 0;

  return { cluster: top.cluster, confidence: margin * coverage };
}
